import { useEffect, useRef } from 'react';

/**
 * Custom hook to auto-resize textarea based on content
 * @param {string} value - The textarea value
 * @param {number} minHeight - Minimum height in pixels (default: auto)
 * @param {number} maxHeight - Maximum height in pixels (default: 200px)
 * @returns {React.RefObject} - Ref to attach to textarea
 */
export const useAutoResize = (value, minHeight = null, maxHeight = 200) => {
  const textareaRef = useRef(null);

  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    // Reset height to auto to get the correct scrollHeight
    textarea.style.height = 'auto';
    
    // Calculate new height
    const scrollHeight = textarea.scrollHeight;
    let newHeight = scrollHeight;
    
    // Apply min height if specified
    if (minHeight !== null) {
      newHeight = Math.max(newHeight, minHeight);
    }
    
    // Apply max height
    if (maxHeight !== null) {
      newHeight = Math.min(newHeight, maxHeight);
    }
    
    textarea.style.height = `${newHeight}px`;
    
    // If content exceeds max height, enable scrolling
    if (scrollHeight > maxHeight) {
      textarea.style.overflowY = 'auto';
    } else {
      textarea.style.overflowY = 'hidden';
    }
  }, [value, minHeight, maxHeight]);

  return textareaRef;
};

/**
 * Handler function for inline use (alternative to hook)
 * @param {Event} e - Input event
 * @param {number} maxHeight - Maximum height in pixels (default: 200px)
 */
export const handleAutoResize = (e, maxHeight = 200) => {
  const textarea = e.target;
  if (!textarea) return;

  // Reset height to auto to get the correct scrollHeight
  textarea.style.height = 'auto';
  
  // Calculate new height
  const scrollHeight = textarea.scrollHeight;
  let newHeight = Math.min(scrollHeight, maxHeight);
  
  textarea.style.height = `${newHeight}px`;
  
  // If content exceeds max height, enable scrolling
  if (scrollHeight > maxHeight) {
    textarea.style.overflowY = 'auto';
  } else {
    textarea.style.overflowY = 'hidden';
  }
};
